// Shared, read-only world renderers.
//
// These draw a scene's world (background, placed furniture/budas, animals)
// from a plain state-like object. They work for BOTH the local player's own state
// (`gameState.raw`) and a visitor's `/visit` view (which has the same
// house/placements/animals shape but trimmed economy and NO catalog of its own —
// callers must pass a catalog where one is needed).
//
// READ-ONLY: no interaction wiring (no context menus, drag, feed/pet). Owner scenes
// keep their own interactive construction; visitor scenes use these helpers to get a
// pixel-identical static view.

import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { sizePlacement, applyPlacementFlip } from '../ui/placementSizing.js';
import { attachClockHand } from '../ui/clockHand.js';
import { placementTexture } from '../ui/seating.js';
import { AnimalSprite } from '../ui/AnimalSprite.js';
import { preloader } from '../scenes/PreloadScene.js';

// Draw the background image for a scene given an already-resolved texture key.
// Matches HomeScene/MainScene: centered, stretched to GAME_WIDTH x GAME_HEIGHT.
export function renderBackground(scene, bgKey) {
  return renderImageWhenReady(scene, bgKey, GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT);
}

/**
 * An image sized to (w, h) that may still be waiting for its art. Staged loading: the
 * scene's own preload() fetched the key, but a download can fail (a deploy restarting the
 * server mid-load, a flaky link). Draw nothing until the key lands — the background loader
 * retries it — instead of Phaser's checkerboard, then swap it in at the same size.
 */
export function renderImageWhenReady(scene, key, x, y, w, h, originX = 0.5, originY = 0.5) {
  const has = scene.textures.exists(key);
  const img = scene.add.image(x, y, has ? key : '__DEFAULT').setOrigin(originX, originY).setDisplaySize(w, h);
  if (!has) preloader.whenReady(key, () => { if (img.active) swapTexture(img, key); });
  return img;
}

/** setTexture that keeps the on-screen size: Phaser keeps the SCALE, so swapping a 32-px
 * placeholder for a 1280-px background would otherwise draw it 40× too big. */
export function swapTexture(img, key) {
  const w = img.displayWidth, h = img.displayHeight;
  img.setTexture(key).setDisplaySize(w, h);
  return img;
}

// Draw furniture/buda placements for one scene (read-only).
//
// placement shape (from gameState.raw.placements / visit view):
//   { id, type: 'furniture'|'buda', item, scene, x, y, seated? }
// Only placements whose `.scene === sceneKey` are drawn. Texture key + size match
// HomeScene exactly: placementTexture(p) (furniture_<item> / buda_<item>) with a `plot_empty` fallback, sized via
// placementSize(item) as a square (default origin 0.5); `perspective`: the room's, for the house rooms (sizePlacement).
export function renderPlacements(scene, placements, sceneKey, { depth = 5, perspective = null } = {}) {
  const sprites = [];
  for (const p of placements ?? []) {
    if (p.scene !== sceneKey) continue;
    const tex = placementTexture(p);
    const sprite = scene.add.image(p.x, p.y, scene.textures.exists(tex) ? tex : 'plot_empty')
      .setDepth(depth);
    sprite.placement = p;   // FurnitureSeats reads the piece off its sprite
    sizePlacement(sprite, p, perspective);
    applyPlacementFlip(sprite, p);
    attachClockHand(scene, sprite, p);  // live hour hand on a wall clock (dies with the dial)
    if (!scene.textures.exists(tex)) preloader.whenReady(tex, () => { if (sprite.active) { sprite.setTexture(tex); sizePlacement(sprite, p, perspective); } });
    sprites.push(sprite);
  }
  return sprites;
}

// Draw animals for one scene (read-only static AnimalSprites — no click handlers).
//
// Animals are tied to their buda placement via `a.buda_placement_id`; an animal
// belongs to a scene iff its buda placement is in that scene. Positioned at
// buda.x + 60, buda.y — identical to HomeScene. `catalog` is forwarded to
// AnimalSprite.update for product-badge art (visit views must pass one, e.g.
// gameState.raw.catalog, since the shared static catalog is the same for everyone).
export function renderAnimals(scene, animals, placements, sceneKey, { catalog = null } = {}) {
  const sprites = [];
  const byScene = (placements ?? []).filter(p => p.scene === sceneKey);
  for (const a of animals ?? []) {
    const buda = byScene.find(p => p.id === a.buda_placement_id);
    if (!buda) continue;
    const sprite = new AnimalSprite(scene, buda.x + 60, buda.y, a);
    sprite.update(a, catalog);
    sprites.push(sprite);
  }
  return sprites;
}
